import CodeIcon from "@mui/icons-material/Code";
import { Button } from "@mui/material";
import { type FC, useEffect, useState } from "react";

import type { IEmbedSize } from "~/feature/embed/utils/getEmbedMinSize";
import { getEmbedSnippet } from "~/feature/embed/utils/getEmbedSnippet";
import { toEmbedConfig } from "~/feature/embed/utils/toEmbedConfig";
import { copyTextToClipboard } from "~/feature/event/components/ActionButtons/utils";

import { useEmbed } from "../../providers/EmbedProvider";
import {
  EMBED_CODE_COPIED,
  EMBED_CODE_COPIED_MS,
  EMBED_CODE_COPY,
  EMBED_CODE_COPY_FAILED,
  EMBED_CODE_LABEL,
  EMBED_CODE_OPEN,
} from "./constants";
import * as Styled from "./EmbedCode.styles";

interface IEmbedCodeProps {
  size: IEmbedSize;
  // Why the current size cannot be embedded, or null when it can.
  fitMessage: string | null;
}

type CopyOutcome = "copied" | "failed";

export const EmbedCode: FC<IEmbedCodeProps> = ({ size, fitMessage }) => {
  const { event, markets, selections, options } = useEmbed();
  const [isOpen, setIsOpen] = useState(false);
  // A fresh object per attempt, so a second copy restarts the timer.
  const [outcome, setOutcome] = useState<{ kind: CopyOutcome } | null>(null);

  useEffect(() => {
    if (!outcome) return;

    const timer = window.setTimeout(
      () => setOutcome(null),
      EMBED_CODE_COPIED_MS
    );

    return () => window.clearTimeout(timer);
  }, [outcome]);

  const snippet = getEmbedSnippet(
    toEmbedConfig({ event, markets, selections, options, size })
  );

  const handleCopy = async () => {
    setOutcome({ kind: (await copyTextToClipboard(snippet)) ? "copied" : "failed" });
  };

  return (
    <Styled.Root>
      <Styled.OpenButton
        variant="contained"
        startIcon={<CodeIcon />}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        {EMBED_CODE_OPEN}
      </Styled.OpenButton>

      {isOpen && (
        <Styled.Panel>
          <Styled.Head>
            {fitMessage ? (
              <Styled.Failed role="status">{fitMessage}</Styled.Failed>
            ) : outcome?.kind === "copied" ? (
              <Styled.Copied role="status">{EMBED_CODE_COPIED}</Styled.Copied>
            ) : outcome?.kind === "failed" ? (
              <Styled.Failed role="status">{EMBED_CODE_COPY_FAILED}</Styled.Failed>
            ) : (
              <span />
            )}
            <Button size="small" onClick={handleCopy} disabled={Boolean(fitMessage)}>
              {EMBED_CODE_COPY}
            </Button>
          </Styled.Head>
          <Styled.Code
            readOnly
            value={snippet}
            aria-label={EMBED_CODE_LABEL}
            onFocus={({ target }) => target.select()}
          />
        </Styled.Panel>
      )}
    </Styled.Root>
  );
};
